import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Sparkles } from "lucide-react";
import { EASE } from "./Reveal";

const SUGGESTIONS = [
  "Welches Gewicht passt zu mir?",
  "Was ist der Wrist Lock?",
  "Wann öffnet der Shop?",
];

function parseSSE(buffer) {
  const parts = buffer.split("\n\n");
  return { complete: parts.slice(0, -1), rest: parts[parts.length - 1] };
}

function RichText({ text }) {
  const lines = text.split("\n");
  return lines.map((line, li) => (
    <span key={li}>
      {li > 0 && <br />}
      {line.split("**").map((seg, si) => (si % 2 ? <strong key={si}>{seg}</strong> : seg))}
    </span>
  ));
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(`chat_${Math.random().toString(36).slice(2, 12)}`);
  const scrollRef = useRef(null);

  const scrollDown = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    const next = [...messages, { role: "user", content: msg }, { role: "assistant", content: "" }];
    setMessages(next);
    scrollDown();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, history, session_id: sessionRef.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { complete, rest } = parseSSE(buffer);
        buffer = rest;
        for (const part of complete) {
          const line = part.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
            scrollDown();
          }
          if (payload.error) {
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: "assistant", content: payload.error };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: "Verbindung unterbrochen — bitte erneut senden." };
        return copy;
      });
    } finally {
      setBusy(false);
      scrollDown();
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="ai-chat-panel"
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="fixed bottom-24 left-5 z-[75] w-[calc(100vw-2.5rem)] max-w-sm border border-white/10 bg-panel shadow-2xl flex flex-col"
            style={{ height: "min(560px, calc(100vh - 8rem))" }}
          >
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <span className="w-9 h-9 flex items-center justify-center bg-gold/15 border border-gold/40 text-gold shrink-0">
                <Sparkles size={16} />
              </span>
              <div className="leading-tight">
                <p className="font-display font-black uppercase tracking-tight text-lg">Fight Concierge</p>
                <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/40">Powered by Claude</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ai-chat-messages">
              {messages.length === 0 && (
                <div className="text-center px-4 py-6">
                  <p className="font-display font-bold uppercase text-white/80 text-lg">Wo kann ich helfen?</p>
                  <div className="mt-4 flex flex-col gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        data-testid="ai-chat-suggestion"
                        onClick={() => send(s)}
                        className="border border-white/10 px-4 py-2.5 text-left text-sm text-slate-300 hover:border-gold/50 hover:text-white transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`ai-chat-message-${m.role}`}
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto bg-gold text-ink font-medium"
                      : "bg-carddark border border-white/10 text-slate-200"
                  }`}
                >
                  {m.role === "user" ? m.content : <RichText text={m.content || (busy && i === messages.length - 1 ? "…" : "")} />}
                </div>
              ))}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-white/10 p-3 flex gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Frage stellen…"
                data-testid="ai-chat-input"
                className="flex-1 bg-black/40 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold transition-colors"
              />
              <button type="submit" disabled={busy} data-testid="ai-chat-send" className="btn-gold !px-4 disabled:opacity-50" aria-label="Senden">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
